import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  X, CaretLeft, CaretRight, Rocket, SquaresFour, Gauge, MagnifyingGlass,
  ChartLineUp, Binoculars, LinkSimple, PenNib, MapPin, Users, Gear,
  CheckCircle, CircleNotch,
} from "@phosphor-icons/react";
import { LogoMark } from "@/components/Logo";

const STORAGE_PREFIX = "jumpseo_tour_done_";
export const OPEN_TOUR_EVENT = "jumpseo:open-tour";

export function openOnboardingTour() {
  window.dispatchEvent(new Event(OPEN_TOUR_EVENT));
}

function buildSteps(hasWorkspace) {
  return [
    {
      icon: Rocket,
      title: "Welcome to JumpSEO",
      body: "JumpSEO is your AI SEO engine: it runs technical SEO audits, tracks your real Google rankings, analyzes competitors, builds genuine backlinks, writes content, and turns visitors into leads — all from one dashboard. This tour walks through every screen, in order. Use Next and Previous any time, or skip it — you can replay it later from the ? button at the top of the screen.",
    },
    hasWorkspace
      ? {
          icon: SquaresFour,
          kind: "projects-info",
          title: "Projects",
          body: "Everything in JumpSEO is organized into Projects — one per website or client you manage. Switch between them any time using the Project panel at the top of the left sidebar, or add a new one there with “+ New project”. The project shown there is the one every other screen works on.",
        }
      : {
          icon: SquaresFour,
          kind: "create-project",
          title: "Let's create your first project",
          body: "Everything in JumpSEO is organized into Projects — one per website or client you manage. You don't have one yet, so let's set one up now. Give it a name (your business name is fine) and, if you know it, your website address. You can fill in the rest later in Settings.",
        },
    {
      icon: SquaresFour,
      title: "Portfolio",
      body: "Portfolio, at the top of the sidebar, is your bird's-eye view across every project you manage — average SEO score, hot leads and follow-ups needed, all in one table. Click any row to jump straight into that project. Managing just one website? You'll mostly live in Dashboard instead.",
    },
    {
      icon: Gauge,
      title: "Dashboard",
      body: "Dashboard is the home screen for whichever project is currently selected. It shows your average SEO score, critical issues found, and your sales pipeline at a glance, plus your most recent SEO audits. It starts empty — running your first audit and adding your first lead is what fills it in.",
    },
    {
      icon: MagnifyingGlass,
      title: "SEO Audit",
      body: "SEO Audit crawls your website and scores it against common technical SEO problems — missing titles, thin content, broken structure — then the AI writes plain-English fixes for each one. Type your website address, choose how many pages to crawl, and click Run Audit. Start small (4–8 pages); you can always crawl deeper later.",
    },
    {
      icon: ChartLineUp,
      title: "Rank Tracking",
      body: "Rank Tracking shows where your pages actually rank on Google, pulled from Google Search Console — the free tool Google itself provides. You'll connect your Search Console account once, then pick your verified property and run a report to see real positions, clicks and impressions for your keywords.",
    },
    {
      icon: Binoculars,
      title: "Competitors",
      body: "Competitors points JumpSEO at a rival's website. It crawls their site, works out which keywords they're targeting, and builds a plan for outranking them. Type a competitor's domain and click Analyze — every analysis is saved so you can come back to it.",
    },
    {
      icon: LinkSimple,
      title: "Link Building",
      body: "Link Building finds real, relevant websites that could link to yours, and drafts a personalized outreach email for each one — never bulk link-exchange schemes, which can get a site penalized. Click Find Prospects to generate a batch, then open one to review or send the AI-drafted email. Sending needs an Instantly account connected in Settings first; until then, drafts are saved for you to send yourself.",
    },
    {
      icon: PenNib,
      title: "Content Studio",
      body: "Content Studio writes long-form, SEO-optimized articles for your blog. Give it a topic and, optionally, target keywords, pick a tone and length, and click Generate Article. From there you can copy the text, or publish straight to a connected WordPress or Shopify blog — set that connection up first in Settings.",
    },
    {
      icon: MapPin,
      title: "Local SEO",
      body: "Local SEO builds a location-specific strategy — target keywords, a Google Business Profile checklist, and content ideas — tailored to the market you set in Settings. Click Generate Plan whenever you want a refreshed strategy.",
    },
    {
      icon: Users,
      title: "Conversion",
      body: "Conversion is your sales pipeline. Add a lead and the AI scores how promising they look and can draft follow-up emails for you. Drag leads through the stages — New, Qualified, Demo, Proposal, Won, Lost — and sync qualified ones straight to Zoho CRM with one click.",
    },
    {
      icon: Gear,
      title: "Settings",
      body: "Settings is where you connect everything: your website domain, target market, Google Analytics, Zoho CRM, WordPress or Shopify publishing, and outreach sending. The Business Profile section matters most — it tells the AI who this project is for, so audits, content and emails don't come out generic. Fill that in first, then connect only the integrations you actually use. Look for ? icons next to the trickier fields.",
    },
    {
      icon: CheckCircle,
      kind: "finish",
      title: "You're all set",
      body: "That's every screen in JumpSEO. If you get stuck, look for the small ? icons next to less obvious fields — mostly in Settings — for a quick explanation. You can replay this tour any time from the ? button at the top of the screen.",
    },
  ];
}

export default function OnboardingTour() {
  const { user, workspaces, workspacesLoaded, loadWorkspaces, selectWorkspace } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [autoChecked, setAutoChecked] = useState(false);
  const [projName, setProjName] = useState("");
  const [projDomain, setProjDomain] = useState("");
  const [creating, setCreating] = useState(false);

  // Auto-show once, the first time a brand-new session has a logged-in user
  // and we know for certain how many workspaces they have.
  useEffect(() => {
    if (!user || !workspacesLoaded || autoChecked) return;
    setAutoChecked(true);
    let done = false;
    try { done = localStorage.getItem(STORAGE_PREFIX + user.id) === "1"; } catch { /* ignore */ }
    if (!done) { setStep(0); setOpen(true); }
  }, [user, workspacesLoaded, autoChecked]);

  // Manual re-open, e.g. from the "?" button in the header or a Settings link.
  useEffect(() => {
    const handler = () => { setStep(0); setOpen(true); };
    window.addEventListener(OPEN_TOUR_EVENT, handler);
    return () => window.removeEventListener(OPEN_TOUR_EVENT, handler);
  }, []);

  if (!open || !workspacesLoaded) return null;

  const hasWorkspace = workspaces.length > 0;
  const steps = buildSteps(hasWorkspace);
  const total = steps.length;
  const current = steps[step];
  const Icon = current.icon;

  const markDone = () => {
    if (user) { try { localStorage.setItem(STORAGE_PREFIX + user.id, "1"); } catch { /* ignore */ } }
  };

  const close = () => { markDone(); setOpen(false); };
  const finish = () => { markDone(); setOpen(false); };
  const next = () => { if (step < total - 1) setStep(step + 1); else finish(); };
  const prev = () => { if (step > 0) setStep(step - 1); };

  const createProject = async () => {
    if (!projName.trim()) { toast.error("Give your project a name first"); return; }
    setCreating(true);
    try {
      const r = await api.createWorkspace({ name: projName.trim(), domain: projDomain.trim() });
      await loadWorkspaces();
      selectWorkspace(r.data.id);
      toast.success("Project created");
      setStep(step + 1);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-testid="onboarding-tour">
      <div className="absolute inset-0" style={{ background: "rgba(2,6,15,0.78)", backdropFilter: "blur(2px)" }} />
      <div key={step} className="fade-up relative w-full max-w-lg border shadow-2xl" style={{ background: "var(--surface)", borderColor: "var(--line)" }}>
        <div className="flex items-center justify-between px-6 pt-5">
          <div className="flex items-center gap-2">
            <LogoMark size={22} />
            <span className="mono text-[11px] uppercase tracking-widest" style={{ color: "var(--text-2)" }}>
              Getting started · {step + 1} of {total}
            </span>
          </div>
          <button data-testid="tour-close" onClick={close} aria-label="Close tour" className="p-1 hover:text-white transition-colors" style={{ color: "var(--text-2)" }}>
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pt-5 pb-2">
          <div className="w-11 h-11 flex items-center justify-center mb-4" style={{ background: "#0b1220", borderRadius: 3 }}>
            <Icon size={24} weight="duotone" color="var(--accent)" />
          </div>
          <h2 className="text-lg font-bold mb-2">{current.title}</h2>
          <p className="text-sm leading-relaxed" style={{ color: "var(--text-2)" }}>{current.body}</p>

          {current.kind === "create-project" && (
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider" style={{ color: "var(--text-2)" }}>Project name</label>
                <input
                  data-testid="tour-project-name"
                  autoFocus
                  value={projName}
                  onChange={(e) => setProjName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && createProject()}
                  placeholder="Acme Plumbing Co."
                  className="w-full text-sm px-3 py-2.5 mt-1 border bg-[#0b1220] outline-none focus:border-[var(--accent)]"
                  style={{ borderColor: "var(--line)" }}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider" style={{ color: "var(--text-2)" }}>Website domain (optional)</label>
                <input
                  data-testid="tour-project-domain"
                  value={projDomain}
                  onChange={(e) => setProjDomain(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && createProject()}
                  placeholder="yourbusiness.com"
                  className="w-full mono text-sm px-3 py-2.5 mt-1 border bg-[#0b1220] outline-none focus:border-[var(--accent)]"
                  style={{ borderColor: "var(--line)" }}
                />
              </div>
              <button
                data-testid="tour-create-project"
                onClick={createProject}
                disabled={creating}
                className="w-full px-4 py-3 text-sm font-bold flex items-center justify-center gap-2 text-white disabled:opacity-60 hover:opacity-90 transition-opacity"
                style={{ background: "var(--primary)" }}
              >
                {creating ? <CircleNotch size={16} className="animate-spin" /> : null}
                {creating ? "Creating…" : "Create project & continue"}
              </button>
              <button onClick={next} className="w-full text-xs hover:text-white transition-colors" style={{ color: "var(--text-2)" }}>
                I'll do this later
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 pb-6 pt-4">
          <div className="flex items-center gap-1.5">
            {steps.map((s, i) => (
              <span key={i} className="block rounded-full transition-colors" style={{ width: 6, height: 6, background: i === step ? "var(--accent)" : "var(--line)" }} />
            ))}
          </div>

          {current.kind !== "create-project" && (
            <div className="flex items-center gap-2">
              {step > 0 && (
                <button data-testid="tour-prev" onClick={prev} className="text-sm px-3 py-2 border flex items-center gap-1 hover:bg-[#0b1220] transition-colors" style={{ borderColor: "var(--line)" }}>
                  <CaretLeft size={14} /> Back
                </button>
              )}
              <button
                data-testid="tour-next"
                onClick={next}
                className="text-sm px-5 py-2 font-bold flex items-center gap-1.5 text-white hover:opacity-90 transition-opacity"
                style={{ background: "var(--primary)" }}
              >
                {step === total - 1 ? "Get started" : "Next"}
                {step < total - 1 && <CaretRight size={14} />}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
